import React, { useEffect, useState } from "react";
import { AndroidLogo, DownloadSimple, X } from "@phosphor-icons/react";
import { API } from "@/lib/api";

/**
 * DriverAppBanner
 * ---------------------------------------------------------------
 * One-line banner shown on the driver dashboard to non-native
 * (browser) users, nudging them to install the Android wrapper so
 * their GPS keeps ticking when the phone locks.
 *
 * Behaviour rules:
 *   - Hidden entirely when running inside the Capacitor app
 *     (`window.Capacitor.isNativePlatform === true`).
 *   - Hidden on iOS and desktop — Android-only for now.
 *   - Dismissible; localStorage remembers the choice for 30 days.
 *   - Only renders when `GET /api/mobile/latest.json` reports the
 *     APK is available (`available: true`).
 */

const DISMISS_KEY = "ridegy.driverAppBanner.dismissedAt";
const DISMISS_MS = 30 * 24 * 60 * 60 * 1000;

function isAndroidBrowser() {
  if (typeof navigator === "undefined") return false;
  return /Android/i.test(navigator.userAgent || "");
}

export default function DriverAppBanner() {
  const [visible, setVisible] = useState(false);
  const [version, setVersion] = useState(null);

  useEffect(() => {
    // Hide if already inside the native wrapper.
    if (window?.Capacitor?.isNativePlatform) return;
    // Only Android browsers see this — installing an .apk on iOS/desktop
    // is a dead-end.
    if (!isAndroidBrowser()) return;

    // Respect the dismiss cooldown.
    try {
      const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
      if (dismissedAt && Date.now() - dismissedAt < DISMISS_MS) return;
    } catch { /* localStorage may be blocked */ }

    // Ask the server whether we've actually shipped an APK.
    fetch(`${API}/mobile/latest.json`, { credentials: "omit" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.available) {
          setVersion(data.version);
          setVisible(true);
        }
      })
      .catch(() => {});
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setVisible(false);
  };

  if (!visible) return null;
  return (
    <div
      data-testid="driver-app-banner"
      className="border-2 border-foreground bg-foreground text-background px-3 py-2 flex items-center gap-3"
    >
      <AndroidLogo size={20} weight="fill" className="shrink-0 text-primary" />
      <div className="flex-1 min-w-0">
        <div className="font-mono text-[10px] uppercase tracking-[0.15em] opacity-70">
          Recommended for drivers
        </div>
        <div className="text-sm leading-tight">
          Install the Android app — keeps GPS on when your phone locks.
        </div>
      </div>
      <a
        href={`${API}/mobile/download`}
        data-testid="driver-app-banner-install"
        className="shrink-0 bg-primary text-foreground border-2 border-background px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.15em] hover:bg-background hover:text-foreground inline-flex items-center gap-1"
      >
        <DownloadSimple size={12} weight="bold" />
        Install{version ? ` v${version}` : ""}
      </a>
      <button
        onClick={dismiss}
        data-testid="driver-app-banner-dismiss"
        className="shrink-0 p-1 text-background/60 hover:text-background"
        aria-label="Dismiss"
      >
        <X size={14} weight="bold" />
      </button>
    </div>
  );
}
